import { flow, getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import { Chat, ChatModel } from "../chat/chat"
import { Contact } from "../contact/contact"
import { withEnvironment } from "../extensions/with-environment"
import { newID, genPrivacyChatID } from "~utils"
import { addDeletedChatId, getDeletedChatIds, removeDeletedChatId } from "~utils/storage/deleted-chat-ids"
import {
  ChatData,
  ChatListData,
  ChatTypes,
  ChatMessagePinnedData,
  Event,
  EventNames,
  UserActionData,
  UserActions,
  ChatMemberAddedData,
  ChatMemberARData,
  ChatMemberLJData,
  MessageTypes,
  MessageStatus,
  ChatNewData,
  MessageData,
  ChatDeleteData,
  ChatsLastMessageData,
  FLAG_ACK,
  MuteData,
  ChatDisplayData,
  ChatBurningSwitchData,
  GameChatUnreadSummaryData,
} from "~proto"
import { NotifyType } from "~types/message"
import { Message, RootStoreModel } from "~models"
// import { Text } from "~components/El"
// import content from "~components/message/content"

type ChatRow = Instance<typeof ChatModel>

const GROUP_LIKE_TYPES = [
  ChatTypes.GROUP,
  ChatTypes.GROUP_MINE,
  ChatTypes.GROUP_DRAGON,
  ChatTypes.CHANNEL,
]

function isGroupLikeType(type?: number) {
  return type != null && GROUP_LIKE_TYPES.includes(type)
}

function normalizeChatDisplayData(data: ChatDisplayData): ChatDisplayData {
  const resolvedType = data.type ?? data.chat_type
  const next: ChatDisplayData =
    resolvedType != null && resolvedType !== data.type ? { ...data, type: resolvedType } : { ...data }

  if (!isGroupLikeType(next.type)) return next

  const chatIdStr = next.chat_id != null ? String(next.chat_id).trim() : ""
  const nameStr = next.name?.trim() || ""
  const idStr = next.id != null ? String(next.id) : ""

  const chatIdIsNumeric = chatIdStr !== "" && /^\d+$/.test(chatIdStr)
  const chatIdLooksLikeCid = chatIdStr !== "" && !chatIdIsNumeric

  /**
   * 规则（群/频道）：
   * - 优先使用服务端给的 `chat_id` 作为会话 key（只要它看起来就是 cid，即非纯数字）。
   * - 若 `chat_id` 是纯数字（历史遗留，把数字 id 当 cid 传），则优先用 `name`（WS cid）作为 key。
   * - `name` 只作为别名保存；不要在 `chat_id` 已是 cid 时用 `name` 覆盖它（两者可能不同）。
   */
  const keyCid =
    chatIdLooksLikeCid ? chatIdStr : (nameStr || (chatIdIsNumeric && chatIdStr !== idStr ? chatIdStr : ""))

  if (!keyCid) return next

  return {
    ...next,
    chat_id: keyCid,
    name: nameStr || keyCid,
  }
}

function isLongWsCid(value: unknown): boolean {
  const s = String(value ?? "").trim()
  return s.length >= 12 && !/^\d+$/.test(s)
}

function resolveCreatedChatServerCid(
  serverReply?: { name?: string; chat_id?: string | number; chat_cid?: string; id?: number } | null,
  localChat?: ChatRow,
): string {
  for (const ref of [serverReply?.chat_cid, serverReply?.name, serverReply?.chat_id]) {
    if (isLongWsCid(ref)) return String(ref).trim()
  }
  for (const ref of [serverReply?.chat_cid, serverReply?.name, serverReply?.chat_id, localChat?.chat_id]) {
    const s = String(ref ?? "").trim()
    if (s) return s
  }
  return String(localChat?.chat_id ?? "")
}

function pickPreferredGroupChat(a: ChatRow, b: ChatRow): ChatRow {
  if ((a.id != null) !== (b.id != null)) {
    return a.id != null ? a : b
  }
  if (isLongWsCid(a.chat_id) && !isLongWsCid(b.chat_id)) return a
  if (isLongWsCid(b.chat_id) && !isLongWsCid(a.chat_id)) return b
  if (a.messages.length !== b.messages.length) {
    return a.messages.length > b.messages.length ? a : b
  }
  const aCanonical = !!(a.name && a.chat_id === a.name)
  const bCanonical = !!(b.name && b.chat_id === b.name)
  if (aCanonical && !bCanonical) return a
  if (bCanonical && !aCanonical) return b
  return a.updated.getTime() >= b.updated.getTime() ? a : b
}

/** 同一群/频道可能以数字 id 与 WS cid（name）两条会话存在，列表按 id 去重 */
function dedupeGroupChats(chats: ChatRow[]): ChatRow[] {
  const result: ChatRow[] = []
  const indexById = new Map<number, number>()
  const canonicalByTitleType = new Map<string, ChatRow>()
  const all = Array.from(chats)

  for (const chat of all) {
    if (!isGroupLikeType(chat.type) || chat.id == null) {
      continue
    }
    const idx = indexById.get(chat.id)
    if (idx == null) {
      indexById.set(chat.id, result.length)
      result.push(chat)
      canonicalByTitleType.set(`${chat.type}:${chat.title}`, chat)
      continue
    }
    result[idx] = pickPreferredGroupChat(result[idx], chat)
    canonicalByTitleType.set(`${chat.type}:${result[idx].title}`, result[idx])
  }

  for (const chat of all) {
    if (!isGroupLikeType(chat.type)) {
      result.push(chat)
      continue
    }
    if (chat.id != null) continue
    const canonical = canonicalByTitleType.get(`${chat.type}:${chat.title}`)
    if (canonical) continue
    result.push(chat)
  }

  return result
}

/** 将阅后即焚时长（秒）格式化为天/小时/分钟/秒，整除时取较大单位 */
function formatBurningDuration(seconds: number): string {
  if (seconds % 86400 === 0) return `${seconds / 86400}天`
  if (seconds % 3600 === 0) return `${seconds / 3600}小时`
  if (seconds % 60 === 0) return `${seconds / 60}分钟`
  return `${seconds}秒`
}

/** 会话列表：未删除、置顶/时间排序、按标题关键字过滤（与 Tab「聊天」一致） */
function filterSortedChatList(chats: Iterable<ChatRow>, key: string): ChatRow[] {
  const data = Array.from(chats).filter((x) => !x.deleted)
  data.sort((a, b) => {
    if (!a.pinTime && !b.pinTime) {
      if (a.lastMessage && b.lastMessage) {
        return b.lastMessage.created.getTime() - a.lastMessage.created.getTime()
      } else if (a.lastMessage && !b.lastMessage) {
        return -1
      } else {
        return 1
      }
    } else if (a.pinTime && b.pinTime) {
      return b.pinTime.getTime() - a.pinTime.getTime()
    } else if (a.pinTime && !b.pinTime) {
      return -1
    } else {
      return 1
    }
  })
  const sorted = key ? data.filter((x) => x.displayUsername && x.displayUsername === key) : data
  return dedupeGroupChats(sorted)
}

/**
 * Model description here for TypeScript hints.
 */
/** 群聊红包上次表单（内存，随 ChatStore；关 App / clear 后清空） */
export type GameGroupRedPacketDraftSnapshot = {
  type: number
  amount: string | null
  count: string | null
  remark: string
  member: unknown
}

export const ChatStoreModel = types
  .model("ChatStore")
  .props({
    chats: types.map(ChatModel),
    current: types.maybe(types.safeReference(ChatModel)),
    /** key: chat_id（字符串） */
    gameGroupRedPacketDrafts: types.optional(types.map(types.frozen()), {}),
  })
  .volatile(() => ({
    rightClickMenuVisible: false,
    rightClickMenuMessageId: "",
    isReplyBarVisible: false,
    deletedChatIds: new Set<string>(),
    chatListHydrated: false,
    pendingChatLastMessageLists: [] as ChatsLastMessageData["items"][],
    pendingGameChatUnreadSummaries: [] as GameChatUnreadSummaryData[],
  }))
  .extend(withEnvironment)
  .views((self) => ({
    get rightClickMenuSelectedMessage(): Message | undefined {
      if (!self.current || !self.rightClickMenuMessageId) return undefined
      return self.current.messages.find(
        ({ message_id }) => message_id === self.rightClickMenuMessageId,
      )
    },
    // 群成员
    get currentMembers() {
      const chat = self.current
      if (!chat) return []
      return chat.members
    },
    search(key: string) {
      return filterSortedChatList(self.chats.values(), key)
    },
    /** 群聊 Tab：普通群、扫雷游戏群、龙虎游戏群（与 `search` 相同排序与关键词过滤） */
    searchGroupChats(key: string) {
      const groupTypes = [ChatTypes.GROUP, ChatTypes.GROUP_MINE, ChatTypes.GROUP_DRAGON]
      return filterSortedChatList(self.chats.values(), key).filter((chat) =>
        groupTypes.includes(chat.type),
      )
    },
    /** 群聊红包表单草稿（按 chat_id） */
    getGameGroupRedPacketDraft(chatCid: string) {
      return self.gameGroupRedPacketDrafts.get(String(chatCid)) as
        | GameGroupRedPacketDraftSnapshot
        | undefined
    },
    /**
     * 根据接收者名称搜索
     * @param key string
     * @returns 
     */
    searchByReceiverUsername(key: string) {
      const allChats = Array.from(self.chats.values()).filter((x) => !x.deleted)
      if (!key) {
        // 输入为空，返回全部聊天
        return allChats
      }

      const query = key.toLowerCase()

      const filtered = allChats.filter((chat) => {
        // 只匹配私聊，并且存在 receiver
        if (!chat.receiver || chat.chatTypeEnum === "GROUP") return false

        const username = chat.receiver.username?.toLowerCase() || ""
        return username.includes(query)
      })

      // 如果没有匹配结果，返回原始聊天列表
      if (filtered.length === 0) {
        return allChats
      }

      // 匹配结果存在，则排序返回
      return filtered.sort((a, b) => {
        // 原来的置顶 + 最新消息排序
        if (!a.pinTime && !b.pinTime) {
          if (a.lastMessage && b.lastMessage) {
            return b.lastMessage.created.getTime() - a.lastMessage.created.getTime()
          } else if (a.lastMessage && !b.lastMessage) {
            return -1
          } else {
            return 1
          }
        } else if (a.pinTime && b.pinTime) {
          return b.pinTime.getTime() - a.pinTime.getTime()
        } else if (a.pinTime && !b.pinTime) {
          return -1
        } else {
          return 1
        }
      })
    },
    
    byId(id: number) {
      return Array.from(self.chats.values()).find((x) => x.id === id)
    },
    byChatId(chat_id: string) {
      return Array.from(self.chats.values()).find((x) => x.chat_id === chat_id)
    },
  }))
  .actions((self) => {
    function setRightClickMenuVisible(visible: boolean) {
      self.rightClickMenuVisible = visible
    }
    function setRightClickMenuMessageId(message_id) {
      self.rightClickMenuMessageId = message_id
    }
    function setIsReplyBarVisible(visible: boolean) {
      self.isReplyBarVisible = visible
    }
    function getChat(chat_id: string | number) {
      return self.chats.get(chat_id.toString())
    }
    function hasChat(chat_id: string | number) {
      return self.chats.has(chat_id.toString())
    }
    function getPrivateChatByUserID(user_id: number) {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { chat_id } = genPrivacyChatID(currentUser.id, user_id)
      return getChat(chat_id)
    }
    function getChatByMessageRef(ref: { chat_cid?: string | null; chat_id?: number | null }) {
      if (ref.chat_cid) {
        const cid = String(ref.chat_cid)
        const byCid = getChat(cid)
        if (byCid) return byCid
        // 某些场景（老链接/短码）会把 chat.name 当作 chat_id 传进来，这里兜底按 name 查找
        const byName = Array.from(self.chats.values()).find((c) => String(c?.name ?? "") === cid)
        if (byName) return byName
        if (/^\d+$/.test(cid)) {
          const byNumericCid = self.byId(Number(cid))
          if (byNumericCid) return byNumericCid
        }
      }
      if (ref.chat_id != null) {
        return self.byId(ref.chat_id) ?? null
      }
      return null
    }
    function _putChat(data: Partial<ChatDisplayData> & { receiver_id?: number }) {
      const normalized = normalizeChatDisplayData(data as ChatDisplayData)
      const { creator_id, receiver_id, members = [], ...item } = normalized
      const now = Date.now()
      normalized.created = normalized.created || now
      normalized.updated = normalized.updated || now
      // 判断聊天室是否已存在
      let chat
      if (hasChat(normalized.chat_id)) {
        // 存在则更新
        chat = getChat(normalized.chat_id)
        if (normalized.id != null && normalized.id > 0) {
          chat.setID(normalized.id)
        }
        if (normalized.type != null) {
          chat.setType(normalized.type)
        }
        if (normalized.creator_id) chat.setCreator(normalized.creator_id)
        chat.setTitle(normalized.title)
        chat.setImage(normalized.image)
        chat.setIsMute(normalized.is_mute)
        chat.setIsBurning(normalized.is_burning)
        chat.setBurningTimer(normalized.burning_timer)
        chat.setAnnouncement(normalized.desc)
        if (normalized.name) chat.setName(normalized.name)
        else if (isGroupLikeType(normalized.type) && normalized.chat_id && !chat.name) {
          chat.setName(String(normalized.chat_id))
        }
        if ("pinned_message_id" in normalized || "pinned_message_content" in normalized) {
          chat.setPinnedMessage(
            (normalized as any).pinned_message_id ?? null,
            (normalized as any).pinned_message_content ?? null,
          )
        }
      } else {
        // 不存在则创建
        chat = self.chats.put({ ...item, creator: creator_id, receiver: receiver_id, desc: normalized?.desc || "" })
      }
      // 同步成员
      if (members.length > 0) {
        chat.membersToUsers({ members })
      }
      
      return chat
    }
    // 新建私聊
    function newPrivateChat(data: Partial<ChatDisplayData> & { receiver_id: number }) {
      data.type = ChatTypes.PRIVATE

      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)

      const { title, chat_id } = genPrivacyChatID(currentUser.id, data.receiver_id)

      return _putChat({ ...data, chat_id, title })
    }
    // 新建群聊 / 频道：优先用服务端下发的长 chat_id（WS cid），短码 name 仅作别名
    function resolveGroupLikeChat(data: ChatDisplayData) {
      const normalized = normalizeChatDisplayData(data)
      const title = normalized.title || (normalized.type === ChatTypes.CHANNEL ? "频道" : "群聊")
      const desc = normalized.desc || ""
      const chatIdStr = normalized.chat_id != null ? String(normalized.chat_id).trim() : ""
      const nameAlias = normalized.name?.trim() || ""
      const chatIdIsNumeric = chatIdStr !== "" && /^\d+$/.test(chatIdStr)
      const chatIdLooksLikeCid = chatIdStr !== "" && !chatIdIsNumeric
      const canonicalCid = chatIdLooksLikeCid
        ? chatIdStr
        : nameAlias || chatIdStr || newID()
      const wsCid = nameAlias || canonicalCid

      if (normalized.id != null) {
        const existing = self.byId(normalized.id)
        if (existing) {
          const preferCid = canonicalCid || existing.chat_id
          const chat = _putChat({
            ...normalized,
            chat_id: preferCid,
            name: wsCid || canonicalCid || existing.name || preferCid,
            title,
            desc,
          })
          if (
            existing.chat_id &&
            existing.chat_id !== preferCid &&
            hasChat(existing.chat_id) &&
            getChat(existing.chat_id) !== chat
          ) {
            mergeChatMessages(getChat(existing.chat_id)!, chat)
            self.chats.delete(existing.chat_id)
          }
          return chat
        }
      }

      if (hasChat(canonicalCid)) {
        return _putChat({ ...normalized, chat_id: canonicalCid, name: wsCid || canonicalCid, title, desc })
      }

      const legacyKey = normalized.chat_id != null ? String(normalized.chat_id) : ""
      if (legacyKey && legacyKey !== canonicalCid && hasChat(legacyKey)) {
        return _putChat({
          ...normalized,
          chat_id: legacyKey,
          name: wsCid || canonicalCid,
          title,
          desc,
        })
      }

      return _putChat({
        ...normalized,
        chat_id: canonicalCid,
        name: wsCid || canonicalCid,
        title,
        desc,
      })
    }
    function newGroupChat(data: ChatDisplayData) {
      return resolveGroupLikeChat({ ...data, title: data.title || "群聊" })
    }
    // 新建频道
    function newChannelChat(data: ChatDisplayData) {
      return resolveGroupLikeChat({ ...data, title: data.title || "频道" })
    }
    function createChatByMessageData(m: MessageData) {
      const { userStore, contactStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { currentUser } = userStore
      const isGroup = m.chat_cid

      if (isGroup) {
        const chat_id = m.chat_cid
        return getChat(chat_id)
      } else {
        const other_id = currentUser.id === m.sender_id ? m.receiver_id : m.sender_id
        if (!other_id) return null
        const user = userStore.users.get(other_id.toString())
        if (!user) return null

        const contact = contactStore.byUserId(other_id)
        return newPrivateChat({
          creator_id: currentUser.id,
          receiver_id: other_id,
          is_mute: contact?.is_mute,
        })
      }
    }
    function createChatByChatData(data: ChatDisplayData) {
      const normalized = normalizeChatDisplayData(data)
      const {
        userStore: { currentUser, addUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      // 群聊
      const isGroup = [ChatTypes.GROUP, ChatTypes.GROUP_MINE, ChatTypes.GROUP_DRAGON].includes(normalized.type)
      // 频道
      const isChannel = normalized.type === ChatTypes.CHANNEL
      if (isGroup) { // 群聊
        return newGroupChat({ ...normalized })
      } else if (isChannel){ // 频道
        return newChannelChat({ ...normalized })
      } else { // 私聊
        const member = normalized.members.find((m) => m.user_id !== currentUser.id)
        if (!member) return null
        addUser(member.user)
        return newPrivateChat({ ...normalized, receiver_id: member.user_id })
      }
    }
    function mergeChatMessages(from: Chat, to: Chat) {
      const seen = new Set(to.messages.map((m) => m.message_id))
      const toAdd: string[] = []
      from.messages.forEach((m) => {
        if (m?.message_id && !seen.has(m.message_id)) {
          toAdd.push(m.message_id)
          seen.add(m.message_id)
        }
      })
      if (toAdd.length) {
        to.messages.push(...(toAdd as any))
      }
    }
    function findPendingLocalGroupChat(data: ChatDisplayData): Chat | undefined {
      const normalized = normalizeChatDisplayData(data)
      if (normalized.id == null) return undefined
      for (const chat of self.chats.values()) {
        if (chat.deleted || !isGroupLikeType(chat.type)) continue
        if (chat.id != null) continue
        if (chat.type !== normalized.type) continue
        if (normalized.title && chat.title !== normalized.title) continue
        if (normalized.creator_id != null && chat.creator?.id !== normalized.creator_id) continue
        return chat
      }
      return undefined
    }
    function dedupeChatStoreGroups() {
      const byId = new Map<number, Chat>()
      const dropKeys: string[] = []

      for (const chat of self.chats.values()) {
        if (!isGroupLikeType(chat.type) || chat.id == null) continue
        const prev = byId.get(chat.id)
        if (!prev) {
          byId.set(chat.id, chat)
          continue
        }
        const keep = pickPreferredGroupChat(prev, chat)
        const drop = keep === prev ? chat : prev
        mergeChatMessages(drop, keep)
        byId.set(chat.id, keep)
        dropKeys.push(drop.chat_id)
      }

      for (const chat of self.chats.values()) {
        if (!isGroupLikeType(chat.type) || chat.id != null || chat.deleted) continue
        for (const canonical of byId.values()) {
          if (canonical.type !== chat.type || canonical.title !== chat.title) continue
          if (canonical.creator?.id != null && chat.creator?.id != null && canonical.creator.id !== chat.creator.id) {
            continue
          }
          mergeChatMessages(chat, canonical)
          dropKeys.push(chat.chat_id)
          break
        }
      }

      dropKeys.forEach((key) => {
        if (self.chats.has(key)) self.chats.delete(key)
      })
    }
    // event-store 获取聊天室列表（与本机持久化“已删除会话”合并）
    const onChatList = flow(function* onChatList({ data: { items } }: Event<ChatListData>) {
      // console.log("onChatList", items)
      self.chatListHydrated = false
      const deletedIds: string[] = yield getDeletedChatIds()
      self.deletedChatIds = new Set(deletedIds)
      items.forEach((item: ChatDisplayData) => {
        const chat = createChatByChatData(item)
        if (chat && self.deletedChatIds.has(chat.chat_id)) {
          chat?.setDeleted(true)
        } else {
          chat?.setDeleted(false)
        }
      })
      dedupeChatStoreGroups()

      self.chatListHydrated = true
      if (self.pendingChatLastMessageLists.length > 0) {
        const pending = self.pendingChatLastMessageLists.slice()
        self.pendingChatLastMessageLists = []
        pending.forEach((pendingItems) => {
          onChatLastMessageList({ data: { items: pendingItems } } as Event<ChatsLastMessageData>)
        })
      }
      if (self.pendingGameChatUnreadSummaries.length > 0) {
        const { messageStore } = getRoot<Instance<typeof RootStoreModel>>(self)
        const pendingSummaries = self.pendingGameChatUnreadSummaries.slice()
        self.pendingGameChatUnreadSummaries = []
        pendingSummaries.forEach((summaryData) => {
          messageStore.onGameChatUnreadSummary({ data: summaryData } as Event<GameChatUnreadSummaryData>)
        })
      }
    })
    // event-store 获取聊天室最后一条消息
    function enqueuePendingGameChatUnreadSummary(data: GameChatUnreadSummaryData) {
      self.pendingGameChatUnreadSummaries.push(data)
    }
    function onChatLastMessageList({ data: { items } }: Event<ChatsLastMessageData>) {
      if (!self.chatListHydrated) {
        self.pendingChatLastMessageLists.push(items)
        return
      }
      const { messageStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      items.forEach((item) => {
        const chat = getChatByMessageRef(item) ?? getChat(item.chat_cid)
        if (chat && chat.messages.length === 0 && !messageStore.messages.get(item.message_id)) {
          messageStore.onLastMessageCreated({ ...item, chat_cid: chat.chat_id })
        }
      })
    }
    // event-store
    function onChatCreated({ data }: Event<ChatDisplayData>) {
      try {
        const pending = findPendingLocalGroupChat(data)
        const chat = createChatByChatData(data)
        if (pending && pending !== chat && hasChat(pending.chat_id)) {
          mergeChatMessages(pending, chat)
          self.chats.delete(pending.chat_id)
        }
        dedupeChatStoreGroups()
        const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
        const { currentUser } = userStore
        const { creator_id } = data
  
        const name =
          currentUser.id === creator_id
            ? "你"
            : userStore.getDisplayNameByUser(userStore.users.get(creator_id))
  
        if ([ChatTypes.GROUP, ChatTypes.GROUP_MINE, ChatTypes.GROUP_DRAGON].includes(data.type)) {
          chat.newMessage({
            content: currentUser.id === creator_id ? `聊天已被${name}创建，可以开始聊天了` : `欢迎来到${chat.title}！${[ChatTypes.GROUP_MINE, ChatTypes.GROUP_DRAGON].includes(data.type) ? "祝大家好运连连！" : ""}`,
            type: MessageTypes.TEXT,
            status: MessageStatus.READ,
            notify_type: NotifyType.CHAT_CREATED,
          })
        }
        if (data.type === ChatTypes.CHANNEL) {
           chat.newMessage({
            content: currentUser.id === creator_id ? `频道已被${name}创建，可以开始聊天了` : `欢迎订阅${chat.title}！`,
            type: MessageTypes.TEXT,
            status: MessageStatus.READ,
            notify_type: NotifyType.CHAT_CREATED,
          })
        }
      } catch (error) {
      }

    }
    // 领取红包消息
    function onChatRedPacketReceive({ data }: Event<ChatDisplayData>) {
      // const chat = createChatByChatData({...data, type: data.chat_type })
      const chat = self.chats.get(data.chat_id)
      // const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      // const { currentUser } = userStore
      // const { redpacket_info } = data

      // const name = currentUser.id === redpacket_info?.sender_id ? "你" : userStore.getDisplayNameByUser(userStore.users.get(redpacket_info?.sender_id))
      if (chat) {
        chat.newMessage({
          // content: `${name}领取了${currentUser.id === redpacket_info?.sender_id ? "自己" : "你"}的红包`,
          content: data?.message || "",
          type: MessageTypes.TEXT,
          status: MessageStatus.READ,
          notify_type: NotifyType.CHAT_CREATED,
        })
      }
    }
    function onChatMemberAdded({ data }: Event<ChatMemberAddedData>) {
      const { chat_cid, items } = data
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { currentUser } = userStore
      const chat = self.chats.get(String(chat_cid))
      // 掃碼加入群組時會先多收到一個 memberAdd 事件
      if (!chat || !items?.length) return

      items.forEach((item) => {
        const { user_id, user } = item
        if (user) {
          userStore.addUser(user)
        }
        const stored = userStore.users.get(user_id.toString())
        const name =
          currentUser.id === user_id
            ? "你"
            : (stored ? userStore.getDisplayNameByUser(stored) : user?.name) || ""
        chat.newMessage({
          content: `${name}加入${chat.type === ChatTypes.CHANNEL ? "频道" : "群聊"}`,
          type: MessageTypes.TEXT,
          status: MessageStatus.READ,
          notify_type: NotifyType.CHAT_MEMBER_ADDED,
        })
      })

      // ChatMember.user 为 safeReference(User)，须先 addUser 再挂 MST 引用（与 chat.membersToUsers 一致）
      const toAdd = items.filter((item) => !chat.members.some((m) => m.user_id === item.user_id))
      if (toAdd.length === 0) return

      const newRows = toAdd.map((member) => {
        const { user: userPayload, ...rest } = member
        const u = userStore.addUser(userPayload)
        return { ...rest, user: u }
      })
      chat.members.replace([...chat.members.slice(), ...newRows] as any)
    }
    function onChatMemberRemoved({ data: { chat_cid, user_ids } }: Event<ChatMemberARData>) {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { currentUser } = userStore
      const chat = self.chats.get(chat_cid)
      if (chat) {
        user_ids.forEach((user_id) => {
          const { user } = chat.members.find((user) => user.user_id === user_id)
          const name = currentUser.id === user_id ? "你" : userStore.getDisplayNameByUser(user)
          chat.newMessage({
            content: `${name}已从${chat.type === ChatTypes.CHANNEL ? "频道" : "群聊"}移除`,
            type: MessageTypes.TEXT,
            status: MessageStatus.READ,
            notify_type: NotifyType.CHAT_MEMBER_REMOVED,
          })
        })
        const newMembers = chat?.members?.filter((i) => user_ids.indexOf(i.user_id) === -1)
        chat.members.replace(newMembers)
      }
    }
    function onChatDeleted({
      data,
    }: {
      data: { chat_cid?: string | null; chat_id?: number | null }
    }) {
      const chat =
        getChatByMessageRef(data) ??
        (data.chat_cid != null ? self.chats.get(String(data.chat_cid)) : null)
      remove(chat)
    }
    function onChatUpdated({ data: { chat_id, ...values } }: Event<ChatData>) {
      const chat = self.chats.get(chat_id.toString()) // 根据ID获取需要更新资料的用户
      chat.update(values)
      return chat
    }
    function onChatMemberLeft({ data: { chat_cid, user_id } }: Event<ChatMemberLJData>) {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { currentUser } = userStore
      const chat = self.chats.get(chat_cid)
      if (chat) {
        const { user } = chat.members.find((user) => user.user_id === user_id)
        const name = currentUser.id === user_id ? "你" : userStore.getDisplayNameByUser(user)
        chat.newMessage({
          content: `${name}已退出群聊`,
          type: MessageTypes.TEXT,
          status: MessageStatus.READ,
          notify_type: NotifyType.CHAT_MEMBER_LEFT,
        })
        const newMembers = chat?.members?.filter((i) => i.user_id !== user_id)
        chat.members.replace(newMembers)
      }
    }
    function onChatBurningSwitched(evt: Event<ChatBurningSwitchData>) {
      const data = evt?.data
      if (!data) return
      const { is_burning, burning_timer, chat_id, chat_cid, creator_id } = data
      let chat = chat_id != null ? self.byId(Number(chat_id)) : undefined
      if (!chat && chat_cid) {
        chat = self.byChatId(chat_cid)
      }
      if (!chat) return

      chat.setIsBurning(is_burning)
      chat.setBurningTimer(burning_timer)

      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const creatorUser =
        creator_id != null ? userStore.users.get(creator_id.toString()) : undefined
      const name =
        creator_id != null && userStore.currentUser.id === creator_id
          ? "你"
          : creatorUser != null
            ? userStore.getDisplayNameByUser(creatorUser)
            : "对方"

      const closed = !is_burning || burning_timer === 0
      chat.newMessage({
        content: closed
          ? `${name}已关闭阅后即焚`
          : `${name}修改阅后即焚为:${formatBurningDuration(burning_timer)}`,
        type: MessageTypes.TEXT,
        status: MessageStatus.READ,
        notify_type: NotifyType.CHAT_BURNING_TIMER_SWITCHED,
      })
    }
    function reconcileCreatedChat(
      localChat: Chat,
      serverReply?: { name?: string; chat_id?: string; chat_cid?: string; id?: number } | null,
    ) {
      if (!localChat) return localChat
      const serverCid = resolveCreatedChatServerCid(serverReply, localChat)
      const serverId = serverReply?.id

      if (serverCid === String(localChat.chat_id)) {
        if (serverId != null) localChat.setID(serverId)
        dedupeChatStoreGroups()
        return localChat
      }

      let serverChat =
        (serverId != null ? self.byId(serverId) : undefined) ??
        getChat(serverCid) ??
        null

      if (!serverChat) {
        serverChat = newGroupChat({
          chat_id: serverCid,
          id: serverId,
          type: localChat.type,
          creator_id: localChat.creator?.id,
          title: localChat.title,
          image: localChat.image,
          desc: localChat.desc ?? "",
          members: localChat.members.map((m) => ({
            user_id: m.user_id,
            user: m.user,
          })) as ChatDisplayData["members"],
        })
      } else {
        if (serverId != null) serverChat.setID(serverId)
        serverChat.setTitle(localChat.title)
        if (localChat.image) serverChat.setImage(localChat.image)
      }

      if (localChat !== serverChat && hasChat(localChat.chat_id)) {
        mergeChatMessages(localChat, serverChat)
        self.chats.delete(localChat.chat_id)
      }

      const shortAlias = serverReply?.name ? String(serverReply.name).trim() : ""
      if (
        shortAlias &&
        shortAlias !== serverCid &&
        hasChat(shortAlias)
      ) {
        const aliasChat = getChat(shortAlias)
        if (aliasChat && aliasChat !== serverChat) {
          mergeChatMessages(aliasChat, serverChat)
          self.chats.delete(shortAlias)
        }
      }

      dedupeChatStoreGroups()
      return serverChat
    }

    function setCurrent(chat: Chat) {
      const prev = self.current
      self.current = chat
      // 切走的会话立即裁剪到最近 N 条（含上拉加载的历史），同步回收 messageStore 全局缓存，
      // 防止逛了几个大群后内存只增不减；重进时普通群从 SQLite 读回、游戏群拉服务端最新一屏
      if (prev && prev.chat_id !== chat?.chat_id) {
        prev.pruneForeignMessages()
        const { messageStore } = getRoot<Instance<typeof RootStoreModel>>(self)
        messageStore.trimInactiveChatMessages(prev)
      }
      chat?.pruneForeignMessages()
    }
    function save(chat: Chat) {
      self.chats.put(chat)
    }
    function clear() {
      self.chats.replace([])
      self.gameGroupRedPacketDrafts.clear()
    }
    function remove(chat: Chat | null | undefined) {
      if (!chat) return
      chat.clearMessages()
      chat.setDeleted(true)

      self.deletedChatIds.add(chat.chat_id)
      void addDeletedChatId(chat.chat_id)
    }

    function reviveChat(chat: Chat) {
      chat.setDeleted(false)
      self.deletedChatIds.delete(chat.chat_id)
      void removeDeletedChatId(chat.chat_id)
    }

    // 正在输入事件处理
    function onTypingStarted() {
      const chat = self.current
      if (!chat) {
        return
      }

      const {
        userStore: { currentUser },
        eventStore,
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      // send event
      const evt = new Event<UserActionData>(EventNames.USER_ACTION, {
        action: UserActions.TYPING_STARTED,
        user_id: currentUser.id,
        chat_id: chat.chat_id,
        chat_cid: chat.id ? chat.id : 0,
        receiver_id: chat.receiver ? chat.receiver.id : 0,
      })
      eventStore.publish(evt)
    }
    function deletePrivateChat(user_id: number) {
      self.chats.delete(user_id.toString())
    }
    function onUserAction({ data: { user_id, chat_id, receiver_id } }: Event<UserActionData>) {
      let chat
      if (receiver_id) {
        chat = self.chats.get(user_id.toString())
      } else {
        chat = self.chats.get(chat_id)
      }
      if (chat) {
        chat.setTyping(user_id)
        setTimeout(() => {
          chat.setTyping(undefined)
        }, 2000)
      }
    }
    function byUsername(account: string) {
      return Array.from(self.chats.values()).find((x) => x.displayTitle === account)
    }
    function onChatMuted({ data: { user_id, chat_id, is_mute } }: Event<MuteData>) {
      const { chatStore, contactStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const chat: Chat = chatStore.byId(chat_id) as any
      if (chat) {
        chat.setIsMute(is_mute)
      } else {
        // is private
        const chats = Array.from(self.chats.values())
        const privateChat: Chat = chats.find((chat) => {
          const { chat_id } = chat
          return Number(chat_id) === user_id
        })
        if (privateChat) {
          privateChat.setIsMute(is_mute)
        }
        const contact: Contact = contactStore.byUserId(user_id)
        if (contact) {
          contact.setIsMute(is_mute)
        }
      }
    }
    function publicNewGroupChat(chat: Chat) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<ChatNewData>(EventNames.CHAT_NEW, {
        type: chat.type,
        chat_id: chat.chat_id,
        title: chat.title,
        image: chat.image,
        members: chat.users.map(({ id }) => id),
      })
      eventStore.publish(evt, { flags: FLAG_ACK })
    }
    function publicLeaveGroupChat(chat: Chat) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<ChatDeleteData>(EventNames.CHAT_MEMBER_LEAVE, {
        chat_id: chat.id,
        chat_cid: chat.chat_id,
      })
      eventStore.publish(evt, { flags: FLAG_ACK })
      remove(chat)
    }
    function publicDismissGroupChat(chat: Chat) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<ChatDeleteData>(EventNames.CHAT_DELETE, {
        chat_id: chat.id,
        chat_cid: chat.chat_id,
      })
      eventStore.publish(evt, { flags: FLAG_ACK })
      remove(chat)
    }
    function onMemberRoleUpdated({ data }) {
      const chat = self.current
      if (!chat) return

      const member = chat.members.find(
        (m) => m.user_id === Number(data.user_id)
      )
      if (!member) return

      if (data.perm !== undefined) {
        member.perm = data.perm
      }

      if (data.is_agent !== undefined) {
        member.is_agent = data.is_agent
      }

      if (data.is_mute !== undefined) {
        member.is_mute = data.is_mute
      }

      if (data.is_collect_bot !== undefined) {
        member.is_collect_bot = data.is_collect_bot
      }

      if (data.is_compensate_bot !== undefined) {
        member.is_compensate_bot = data.is_compensate_bot
      }
    }
    function onChannelChatInfo({ data }) {
      const id = Number(data?.chat_id)
      const chat =
        (Number.isFinite(id) ? self.byId(id) : undefined) ||
        (data?.chat_id != null ? self.byChatId(String(data.chat_id)) : undefined) ||
        (self.current?.id === id ? self.current : undefined)
      if (!chat) return
      chat.setChannelChatInfo(data)
    }
    // 更新频道信息关联担保群配置
    function onChannelChatInfoLinkGroupUpdated(data) {
      const channelChatInfo = self.current?.channelChatInfo
      if (!channelChatInfo) return

      channelChatInfo.updateLinkGroupActive(
        Number(data.group_id),
        data.is_active
      )
    }
    function onChatAnnouncemented({ data }) {
      const chat = self.chats.get(data.chat_id)
      if (!chat) return
      chat.setAnnouncement(data.title)
      chat.newMessage({
        content: `公告已更新`,
        type: MessageTypes.TEXT,
        status: MessageStatus.READ,
        notify_type: NotifyType.CHAT_ANNOUNCEMENTED,
      })
    }

    function setGameGroupRedPacketDraft(chatCid: string, draft: GameGroupRedPacketDraftSnapshot) {
      self.gameGroupRedPacketDrafts.set(String(chatCid), draft as any)
    }

    function onChatMessagePinned({ data }: Event<ChatMessagePinnedData>) {
      const chat = self.chats.get(String(data.chat_id))
      if (!chat) return

      const pinnedId = Number(data.message_id || 0)
      if (pinnedId === 0) {
        chat.setPinnedMessage(null, null)
      } else {
        chat.setPinnedMessage(pinnedId, data.content ?? null)
      }
    }
    return {
      setRightClickMenuVisible,
      setRightClickMenuMessageId,
      setIsReplyBarVisible,
      getChat,
      getChatByMessageRef,
      hasChat,
      getPrivateChatByUserID,
      _putChat,
      newPrivateChat,
      newGroupChat,
      newChannelChat,
      createChatByMessageData,
      createChatByChatData,
      onChatList,
      enqueuePendingGameChatUnreadSummary,
      onChatLastMessageList,
      onChatCreated,
      onChatRedPacketReceive,
      onChatMemberAdded,
      onChatMemberRemoved,
      onChatDeleted,
      onChatUpdated,
      onChatMemberLeft,
      onChatBurningSwitched,
      reconcileCreatedChat,
      setCurrent,
      save,
      clear,
      remove,
      reviveChat,
      onTypingStarted,
      deletePrivateChat,
      onUserAction,
      onChatMuted,
      publicNewGroupChat,
      publicLeaveGroupChat,
      publicDismissGroupChat,
      byUsername,
      onMemberRoleUpdated,
      onChannelChatInfo,
      onChannelChatInfoLinkGroupUpdated,
      onChatAnnouncemented,
      onChatMessagePinned,
      setGameGroupRedPacketDraft,
    }
  })

type ChatStoreType = Instance<typeof ChatStoreModel>
export interface ChatStore extends ChatStoreType {}
type ChatStoreSnapshotType = SnapshotOut<typeof ChatStoreModel>
export interface ChatStoreSnapshot extends ChatStoreSnapshotType {}
export const createChatStoreDefaultModel = () => types.optional(ChatStoreModel, {})
